import React from 'react';

interface TruncatedTextProps {
  text: string;
  maxLength?: number;
  maxLines?: number;
  className?: string;
  showTooltip?: boolean;
}

/**
 * TruncatedText component that truncates text based on maxLength or maxLines
 * Shows default browser tooltip with full content on hover
 */
export const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  maxLength,
  maxLines,
  className = '',
  showTooltip = true,
}) => {
  // Check if text needs truncation
  const needsTruncation = 
    (maxLength && text.length > maxLength) ||
    (maxLines && text.split('\n').length > maxLines);

  // Truncate text based on maxLength
  const getTruncatedText = () => {
    if (maxLength && text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  // Get line-clamp class for maxLines
  const getLineClampClass = () => {
    if (maxLines) {
      // Use Tailwind's line-clamp utilities (1-6 are standard)
      const lineClampMap: Record<number, string> = {
        1: 'line-clamp-1',
        2: 'line-clamp-2',
        3: 'line-clamp-3',
        4: 'line-clamp-4',
        5: 'line-clamp-5',
        6: 'line-clamp-6',
      };
      return lineClampMap[maxLines] || `line-clamp-${Math.min(maxLines, 6)}`;
    }
    return '';
  };

  if (!text) {
    return <span className={className}>-</span>;
  }

  const displayText = getTruncatedText();
  const lineClampClass = getLineClampClass();

  // For line-clamp to work properly, we need specific CSS properties
  const lineClampStyle = maxLines ? {
    display: '-webkit-box',
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } : {};

  return (
    <span
      className={`${className} ${lineClampClass} ${maxLines ? 'break-words' : ''}`}
      style={lineClampStyle}
      title={needsTruncation && showTooltip ? text : undefined}
    >
      {displayText}
    </span>
  );
};
